import { z } from 'zod/v3';
import type { SpecificClientPriceRangePaymentType } from '@/types/commercial/specific-client-prices';

const monetaryOptional = z.coerce.number().min(0).optional();
export const OPEN_ENDED_QUANTITY_TO = 2_147_483_647;

export const specificClientPriceSchema = z.object({
  priceCash: monetaryOptional,
  priceInstallment: monetaryOptional,
  priceMonthly: monetaryOptional,
  priceYearly: monetaryOptional,
  priceFixed: monetaryOptional,
});

export const specificClientPriceRangeSchema = z.object({
  paymentType: z.enum(['CASH', 'INSTALLMENT', 'INSTALLMENTS']),
  quantityFrom: z.number().int().min(1),
  quantityTo: z.number().int().min(1).optional(),
  priceInCents: z.number().int().min(0),
});

export const specificClientPriceRangesSchema = z
  .array(specificClientPriceRangeSchema)
  .superRefine((ranges, ctx) => {
    const rangesByPayment = new Map<
      SpecificClientPriceRangePaymentType,
      z.infer<typeof specificClientPriceRangeSchema>[]
    >();

    for (const range of ranges) {
      rangesByPayment.set(range.paymentType, [
        ...(rangesByPayment.get(range.paymentType) ?? []),
        range,
      ]);
    }

    for (const rangesForPayment of rangesByPayment.values()) {
      const sorted = [...rangesForPayment].sort((a, b) => a.quantityFrom - b.quantityFrom);

      sorted.forEach((range, index) => {
        if (index === 0 && range.quantityFrom !== 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A primeira faixa deve iniciar em 1',
            path: [index, 'quantityFrom'],
          });
        }

        const quantityTo = range.quantityTo;
        const isOpenEnded = quantityTo === undefined;

        if (isOpenEnded && index !== sorted.length - 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Faixa aberta só pode existir na última posição',
            path: [index, 'quantityTo'],
          });
        }

        if (quantityTo !== undefined) {
          if (quantityTo < range.quantityFrom) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'A quantidade final deve ser maior ou igual a inicial',
              path: [index, 'quantityTo'],
            });
          }
        }

        const previous = sorted[index - 1];
        if (previous) {
          if (previous.quantityTo === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Não pode haver faixas após uma faixa aberta',
              path: [index, 'quantityFrom'],
            });
          } else if (range.quantityFrom !== previous.quantityTo + 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'As faixas devem ser contínuas, sem lacunas ou sobreposição',
              path: [index, 'quantityFrom'],
            });
          }
        }
      });
    }
  });

export type SpecificClientPriceFormData = z.infer<typeof specificClientPriceSchema>;
